import { queryOptions, useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import { type Substantivo } from '@/lib/camadasGestao'
import type { ModoDoTooltip, PosicaoDoTooltip, TamanhoDoTooltip, Trecho } from '@/lib/tooltips'
import { localDaCamada } from '@/lib/tooltips'

/*
 * A aba Conteúdos da Área do Gestor. É estado de servidor: a lista vive no
 * cache do TanStack Query e as ações (enviar, testar, publicar, editar,
 * excluir) a invalidam, então a tela mostra o que a API tem, e não uma cópia
 * que ela mesma editou.
 */

/** O caminho do tooltip: da fila ao observatório. O excluído a API não devolve. */
export type SituacaoDoTooltip = 'aguardando-teste' | 'testado' | 'publicado'

/** Um tooltip, como `GET /v1/admin/tooltips` o devolve. */
export interface TooltipDaGestao {
  id: number
  /** O ponto fixo do observatório (`camada-vc`); nulo no tooltip solto. */
  local: string | null
  /** O texto sem formato: a soma dos trechos. */
  texto: string
  trechos: readonly Trecho[]
  modo: ModoDoTooltip
  tamanho: TamanhoDoTooltip
  /** Onde o solto fica, em % da área do observatório; nulo no fixo. */
  posicao: PosicaoDoTooltip | null
  situacao: SituacaoDoTooltip
  /** Quem enviou ou salvou por último; nulo nos dez textos da publicação inicial. */
  escritoPor: string | null
}

/** O corpo de `GET /v1/admin/tooltips`. */
export interface AbaDeConteudos {
  lista: TooltipDaGestao[]
  /** Só o administrador testa e publica; o editor de conteúdo só envia. */
  podeTestarEPublicar: boolean
}

const CHAVE = ['admin', 'tooltips'] as const

/** Sem nova tentativa: erro de servidor deve aparecer logo, com o botão "Tentar de novo". */
export const tooltipsDaGestaoQuery = queryOptions({
  queryKey: CHAVE,
  queryFn: () => apiFetch<AbaDeConteudos>('/v1/admin/tooltips'),
  retry: false,
})

export function useTooltipsDaGestao() {
  return useQuery(tooltipsDaGestaoQuery)
}

export const TOOLTIP: Substantivo = ['tooltip', 'tooltips']

/** A fila: o tooltip que espera o teste e o que, testado, espera a publicação. */
export function pendentesDaFila(lista: readonly TooltipDaGestao[]): TooltipDaGestao[] {
  return lista.filter((t) => t.situacao === 'aguardando-teste' || t.situacao === 'testado')
}

/**
 * O nome de cada local fixo na tabela. A ficha do imóvel tem nome próprio; os
 * locais das camadas levam o nome da camada no catálogo, que é dado do
 * servidor, então o local acompanha o catálogo se ele mudar.
 */
export function nomesDosLocais(
  catalogo: readonly { codigo: string; nome: string }[] | undefined
): ReadonlyMap<string, string> {
  const nomes = new Map<string, string>([['ficha-vc', 'Ficha do imóvel · Valor de Conservação']])
  for (const camada of catalogo ?? []) {
    const local = localDaCamada(camada.codigo)
    if (local !== undefined) nomes.set(local, `Camadas · ${camada.nome}`)
  }
  return nomes
}

/** O que a coluna Local mostra: o nome do ponto fixo, ou "No observatório" para o solto. */
export function rotuloDoLocal(
  tooltip: Pick<TooltipDaGestao, 'local'>,
  nomes: ReadonlyMap<string, string>
): string {
  if (tooltip.local === null) return 'No observatório'
  return nomes.get(tooltip.local) ?? tooltip.local
}

/** O que a coluna Como aparece mostra. */
export function rotuloDoModo(modo: ModoDoTooltip): string {
  return modo === 'icone' ? 'Ícone i' : 'Ao passar o mouse'
}

/** O que a coluna Tamanho e o botão do tamanho mostram. */
export function rotuloDoTamanho(tamanho: TamanhoDoTooltip): string {
  return { menor: 'Menor', padrao: 'Padrão', maior: 'Maior' }[tamanho]
}

/** O que a coluna Escrito por mostra; os dez textos da publicação inicial não têm autor. */
export function rotuloDeQuemEscreveu(escritoPor: string | null): string {
  return escritoPor ?? 'Publicação inicial'
}

/**
 * A tabela: o que já está no ar. Os fixos seguem a ordem dos locais no
 * observatório (a ficha do imóvel e depois as camadas na ordem do catálogo), e
 * não a da API, que os entrega por nome de local; o fixo de local que o catálogo
 * não conhece vai depois deles e os soltos por último, como a API os numera.
 */
export function publicadosDaTabela(
  lista: readonly TooltipDaGestao[],
  nomes: ReadonlyMap<string, string>
): TooltipDaGestao[] {
  const ordem = [...nomes.keys()]
  const posto = (t: TooltipDaGestao) => {
    if (t.local === null) return ordem.length + 1
    const i = ordem.indexOf(t.local)
    return i === -1 ? ordem.length : i
  }
  return lista.filter((t) => t.situacao === 'publicado').sort((a, b) => posto(a) - posto(b))
}
